import { ref, watch } from "vue";
import axios from "axios";
import { globalSearch, } from "@/modules/header/infrastructure/api/globalSearchApi";
import { logger } from "@/infrastructure/services/LoggerService";
/** Debounce window (ms) before a typed query hits the BFF. */
export const GLOBAL_SEARCH_DEBOUNCE_MS = 250;
/** Minimum query length before any request is issued (shorter ⇒ empty results, no call). */
export const GLOBAL_SEARCH_MIN_LENGTH = 3;
/** First (and only) page requested by the quick search, with its page size. */
const GLOBAL_SEARCH_PAGE = 1;
const GLOBAL_SEARCH_PAGE_SIZE = 20;
/** Empty result shape reused whenever the query is below threshold or cleared. */
function emptyResult() {
    return { contacts: [], organisations: [], totalContacts: 0, totalOrganisations: 0 };
}
/**
 * Header global-search composable (logic only — the result dropdown is separate UI).
 *
 * Exposes a `query` ref: writing to it debounces (~250ms) and, once the trimmed
 * value reaches {@link GLOBAL_SEARCH_MIN_LENGTH} characters, calls
 * `GET /api/v1/portal/global-search`. Below the threshold the results are cleared
 * and no request is made. Stale in-flight requests are aborted on each new search
 * (race-safe), mirroring `useContactSearch` / `useOrganisationSearch`.
 *
 * `search(term)` runs the same debounced pipeline imperatively (and keeps `query`
 * in sync); `clear()` cancels any pending call and resets state.
 */
export function useGlobalSearch() {
    const query = ref("");
    const results = ref(emptyResult());
    const isLoading = ref(false);
    const error = ref(false);
    let debounceTimer = null;
    let abortController = null;
    let requestId = 0;
    // Guards the `query` watcher against re-entering `search` on the write `search`
    // itself performs to keep `query` in sync.
    let syncingQuery = false;
    function cancelPending() {
        if (debounceTimer !== null) {
            clearTimeout(debounceTimer);
            debounceTimer = null;
        }
        abortController?.abort();
        abortController = null;
    }
    async function runSearch(term) {
        abortController = new AbortController();
        const currentId = ++requestId;
        isLoading.value = true;
        error.value = false;
        try {
            const response = await globalSearch(term, GLOBAL_SEARCH_PAGE, GLOBAL_SEARCH_PAGE_SIZE, abortController.signal);
            if (currentId !== requestId)
                return;
            results.value = response;
        }
        catch (err) {
            if (axios.isCancel(err))
                return;
            logger.error("Global search failed", err);
            results.value = emptyResult();
            error.value = true;
        }
        finally {
            if (currentId === requestId)
                isLoading.value = false;
        }
    }
    function search(term) {
        syncingQuery = true;
        query.value = term;
        syncingQuery = false;
        // The new (or below-threshold) query supersedes any pending timer / in-flight call.
        cancelPending();
        if (term.trim().length < GLOBAL_SEARCH_MIN_LENGTH) {
            results.value = emptyResult();
            isLoading.value = false;
            error.value = false;
            return;
        }
        debounceTimer = setTimeout(() => {
            void runSearch(term.trim());
        }, GLOBAL_SEARCH_DEBOUNCE_MS);
    }
    function clear() {
        cancelPending();
        // Invalidate any settling request so a late resolve can't repopulate results.
        requestId += 1;
        syncingQuery = true;
        query.value = "";
        syncingQuery = false;
        results.value = emptyResult();
        isLoading.value = false;
        error.value = false;
    }
    // Keep `query` as the single source of truth: external writes drive the search too.
    watch(query, (value) => {
        if (syncingQuery)
            return;
        search(value);
    });
    return { query, results, isLoading, error, search, clear };
}
